import { match } from 'ts-pattern';
import { createRecipe, implementCustomExpressions } from '../__generated/implementation.js';
import { HandrailEndType, HandrailPossibleWallEndType } from '../__generated/model/index.js';
import { getBalustrade } from '../projections/balustrade.js';
export const stairExpressions = implementCustomExpressions({
    Stairs: {
        $height: (s)=>{
            return s.riserDistance * s.numberOfSteps + 1;
        },
        $centerX: (s)=>{
            return s.width / 2;
        },
        $centerZ: (s)=>{
            return s.$depth / 2;
        },
        $slope: (s)=>{
            return Math.atan(s.riserDistance / s.goingDistance);
        },
        $depth: (s)=>{
            return s.goingDistance * (s.numberOfSteps - 1);
        },
        $levelHeight: (s)=>{
            return s.$deck.$shapeHeights.levels[s.id];
        },
        $balustrades: (s, state, ctx)=>{
            const shape = ctx.sketchingProjections.getEntityRectangle(s.id, state, 'main');
            const leftBalustrade = match(s.balustradeLeft).with(true, ()=>{
                const leftConnection = shape.topLeft.connection;
                const leftConnectionEntity = leftConnection?.from.shape.instance.entityId ? ctx.domain.get$Entity(leftConnection.from.shape.instance.entityId, state) : undefined;
                const rightConnection = shape.bottomLeft.connection;
                const rightConnectionEntity = rightConnection?.to.shape.instance.entityId ? ctx.domain.get$Entity(rightConnection.to.shape.instance.entityId, state) : undefined;
                const key = `${s.id}_rectangle-left_balustrade`;
                const reversed = false;
                const edgeOffset = 100;
                const balustrade = getBalustrade(shape, leftConnection, leftConnectionEntity, rightConnection, rightConnectionEntity, key, s.$depth, s.$deck.$maxBalustradeSectionLength, s.$deck.$maxPostDistance, s.$deck.useThinIntermediatePosts, s.goingDistance, s.riserDistance, reversed, edgeOffset, s.$height, s.$deck.handrailType, s.$deck.postType);
                return [
                    balustrade
                ];
            }).with(false, ()=>{
                return [];
            }).exhaustive();
            const rightBalustrade = match(s.balustradeRight).with(true, ()=>{
                const leftConnection = shape.bottomRight.connection;
                const leftConnectionEntity = leftConnection?.from.shape.instance.entityId ? ctx.domain.get$Entity(leftConnection.from.shape.instance.entityId, state) : undefined;
                const rightConnection = shape.topRight.connection;
                const rightConnectionEntity = rightConnection?.to.shape.instance.entityId ? ctx.domain.get$Entity(rightConnection.to.shape.instance.entityId, state) : undefined;
                const key = `${s.id}_rectangle-right_balustrade`;
                const reversed = true;
                const edgeOffset = s.width - 100;
                const balustrade = getBalustrade(shape, leftConnection, leftConnectionEntity, rightConnection, rightConnectionEntity, key, s.$depth, s.$deck.$maxBalustradeSectionLength, s.$deck.$maxPostDistance, s.$deck.useThinIntermediatePosts, s.goingDistance, s.riserDistance, reversed, edgeOffset, s.$height, s.$deck.handrailType, s.$deck.postType);
                return [
                    balustrade
                ];
            }).with(false, ()=>{
                return [];
            }).exhaustive();
            return [
                ...leftBalustrade,
                ...rightBalustrade
            ];
        },
        $posts: (s)=>{
            const posts = s.$balustrades.flatMap((b)=>b.sections).flatMap((section)=>section.posts.flatMap((post)=>{
                    if (post.shared) {
                        return [];
                    }
                    return [
                        createRecipe({
                            type: 'Post',
                            key: post.key,
                            deckItemId: s.id,
                            positionType: post.positionType,
                            x: section.edgeOffset,
                            y: post.y,
                            z: post.x,
                            slope: post.slope
                        })
                    ];
                }));
            return posts;
        },
        $fixingSystems: (s)=>{
            return s.$balustrades.flatMap((b)=>b.sections).flatMap((section, i)=>{
                const leftpost = section.reverse ? section.posts[section.posts.length - 1] : section.posts[0];
                const rightPost = section.reverse ? section.posts[0] : section.posts[section.posts.length - 1];
                return Array.from({
                    length: s.$deck.$numberOfWires - 1
                }, (_, j)=>{
                    return createRecipe({
                        type: 'FixingSystem',
                        key: `${section.key}_fixing_system_${i}_${j}`,
                        deckItemId: s.id,
                        leftX: leftpost.x,
                        leftY: leftpost.y,
                        leftZ: section.edgeOffset,
                        leftPositionType: section.left.positionType,
                        rightX: rightPost.x,
                        rightY: rightPost.y,
                        rightZ: section.edgeOffset,
                        rightPositionType: section.right.positionType,
                        rightAngle: section.right.angle,
                        sectionSlope: section.slope,
                        order: j
                    });
                });
            });
        },
        $handrails: (s)=>{
            const handrails = s.$balustrades.flatMap((balustrade)=>{
                const recipes = [];
                const handrail = createRecipe({
                    type: 'Handrail',
                    key: `${balustrade.key}_handrail`,
                    deckItemId: s.id,
                    x: balustrade.edgeOffset,
                    y: balustrade.handrail.handrailHeight,
                    z: balustrade.handrail.handrailStart,
                    rotationX: 0,
                    rotationY: -Math.PI / 2,
                    rotationZ: balustrade.handrail.slope,
                    length: balustrade.handrail.handrailLength,
                    leftPositionType: balustrade.left.positionType,
                    rightPositionType: balustrade.right.positionType,
                    numberOfSections: balustrade.handrail.numberOfSections,
                    balustradeLength: balustrade.handrail.balustradeLength
                });
                recipes.push(handrail);
                const leftEnd = createRecipe({
                    type: 'HandrailEnd',
                    key: `${balustrade.key}_handrail_end_left`,
                    deckItemId: s.id,
                    x: balustrade.edgeOffset,
                    y: balustrade.handrail.leftEndHeight,
                    z: balustrade.handrail.leftEndX,
                    positionType: balustrade.left.positionType,
                    possibleWallEndType: HandrailPossibleWallEndType.Free,
                    endType: HandrailEndType.FlatEndCap,
                    angle: balustrade.left.angle,
                    slope: balustrade.left.slope
                });
                const rightEnd = createRecipe({
                    type: 'HandrailEnd',
                    key: `${balustrade.key}_handrail_end_right`,
                    deckItemId: s.id,
                    x: balustrade.edgeOffset,
                    y: balustrade.handrail.rightEndHeight,
                    z: balustrade.handrail.rightEndX,
                    positionType: balustrade.right.positionType,
                    possibleWallEndType: HandrailPossibleWallEndType.Free,
                    endType: HandrailEndType.FlatEndCap,
                    angle: balustrade.right.angle,
                    slope: balustrade.right.slope
                });
                recipes.push(leftEnd);
                if (!balustrade.handrail.sharedRightEnd) {
                    recipes.push(rightEnd);
                }
                return recipes;
            });
            return handrails;
        }
    }
});

//# sourceMappingURL=stairs.js.map